import {
    formNameLabel,
    scheduleEmbedLabel,
    schedulePreviewEmbed,
} from "@/features/navigation/services/entityDisplayLabel";
import { supabaseClient } from "@/lib/supabase";
import { queryOptions, useQuery } from "@tanstack/react-query";

export interface ValidationDetailT {
    validation_id: string;
    is_valid: boolean | null;
    validated_by: string;
    validated_by_name: string | null;
    validated_by_email: string | null;
    entity_type: "form" | "schedule";
    entity_id: string;
    entity_name: string;
    entity_description: string | null;
}

const validationDetailSelect = `validation_id, is_valid, validated_by, profiles(first_name, last_name, email), form_validations(form_id, forms(form_name, form_description, slug)), schedule_validations(schedule_id, schedules(schedule_description, ${schedulePreviewEmbed}))`;

function assignedFromProfiles(
    profiles: { first_name: string | null; last_name: string | null; email: string | null } | null
) {
    const name = [profiles?.first_name, profiles?.last_name].filter(Boolean).join(" ").trim();
    return {
        validated_by_name: name || null,
        validated_by_email: profiles?.email ?? null,
    };
}

export async function selectValidationById(validationId: string): Promise<ValidationDetailT | null> {
    const { data, error } = await supabaseClient
        .from("validations")
        .select(validationDetailSelect)
        .eq("validation_id", validationId)
        .maybeSingle();
    if (error) throw error;
    if (!data) return null;

    const assigned = assignedFromProfiles(data.profiles);
    const formLink = data.form_validations?.[0];
    if (formLink?.form_id) {
        return {
            validation_id: data.validation_id,
            is_valid: data.is_valid,
            validated_by: data.validated_by,
            ...assigned,
            entity_type: "form",
            entity_id: formLink.form_id,
            entity_name: formNameLabel(formLink.forms),
            entity_description: formLink.forms?.form_description ?? null,
        };
    }
    const scheduleLink = data.schedule_validations?.[0];
    if (scheduleLink?.schedule_id) {
        return {
            validation_id: data.validation_id,
            is_valid: data.is_valid,
            validated_by: data.validated_by,
            ...assigned,
            entity_type: "schedule",
            entity_id: scheduleLink.schedule_id,
            entity_name: scheduleEmbedLabel(scheduleLink.schedules),
            entity_description: scheduleLink.schedules?.schedule_description ?? null,
        };
    }
    return null;
}

export const validationByIdQueryOptions = (validationId: string) =>
    queryOptions({
        queryKey: ["validations", "detail", validationId],
        queryFn: () => selectValidationById(validationId),
        enabled: Boolean(validationId),
    });

export function useValidationByIdQuery(validationId: string) {
    return useQuery(validationByIdQueryOptions(validationId));
}
